import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { supabase } from '@/lib/supabase';
import { GENERAL_STATUS } from '@/lib/attendanceTypes';

// Module-level constants - never recreated
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const year = CURRENT_YEAR - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

/**
 * Hook to load entitlements page data with server-side pagination and filtering
 * Replaces 4 queries (1 sequential + 3 parallel) + O(n²) client-side join with 1 optimized RPC call
 *
 * @param {string} companyId - Company ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {number} options.debounceMs - Search debounce delay (default 300ms)
 * @returns {Object} - { entitlements, stats, teamMembers, customTypes, loading, error, pagination, filters, refetch }
 */
export function useEntitlements(companyId, options = {}) {
  const { pageSize: initialPageSize = 25, debounceMs = 300 } = options;

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() => getStoredPageSize(initialPageSize));
  const setPageSize = useCallback((val) => { _setPageSize(val); setStoredPageSize(val); }, []);

  // Filter state - default to current year
  const [yearFilter, setYearFilter] = useState(CURRENT_YEAR.toString());
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for request management
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs]);

  // Reset page when year filter changes
  useEffect(() => {
    setPage(1);
  }, [yearFilter]);

  /**
   * Load entitlements data from server
   */
  const loadData = useCallback(async () => {
    if (!companyId) {
      console.log('[useEntitlements] Missing companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useEntitlements] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useEntitlements] Loading data with single RPC call...');
      console.time('[useEntitlements] RPC call duration');

      const offset = (page - 1) * pageSize;

      // Build filter params - 'all' means no filter
      const yearParam = yearFilter && yearFilter !== 'all' ? parseInt(yearFilter, 10) : null;
      const searchParam = debouncedSearch || null;

      // Single RPC call replaces 4 queries + client-side O(n²) join!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_entitlements_page',
        {
          p_company_id: companyId,
          p_year: yearParam,
          p_search: searchParam,
          p_limit: pageSize,
          p_offset: offset
        }
      );

      console.timeEnd('[useEntitlements] RPC call duration');

      if (rpcError) {
        console.error('[useEntitlements] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useEntitlements] RPC response:', {
        entitlementsCount: rpcData?.entitlements?.length || 0,
        teamMembersCount: rpcData?.team_members?.length || 0,
        customTypesCount: rpcData?.custom_types?.length || 0,
        totalCount: rpcData?.total_count || 0,
        filteredCount: rpcData?.filtered_count || 0,
        stats: rpcData?.stats
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useEntitlements] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, page, pageSize, yearFilter, debouncedSearch]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Change page size and reset to first page
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  /**
   * Set year filter
   */
  const handleYearChange = useCallback((year) => {
    setYearFilter(year);
  }, []);

  /**
   * Calculate total days for an entitlement (memoizable helper)
   */
  const getTotalDays = useCallback((entitlement) => {
    if (!entitlement) return 0;
    // If pre-computed by RPC, use that
    if (entitlement.total_days !== undefined) {
      return entitlement.total_days;
    }
    // Fallback calculation
    const customTotal = Object.values(entitlement.custom_entitlements || {})
      .reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    return (entitlement.annual_leave_days || 0) + (entitlement.carry_over_days || 0) + customTotal;
  }, []);

  // Computed pagination values
  const totalPages = useMemo(() => {
    const filteredCount = data?.filtered_count || 0;
    return Math.ceil(filteredCount / pageSize);
  }, [data?.filtered_count, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    entitlements: data?.entitlements || [],
    teamMembers: data?.team_members || [],
    customTypes: data?.custom_types || [],
    stats: data?.stats || {
      total_entitlements: 0,
      current_year_count: 0,
      average_days: 0,
      with_carry_over: 0
    },

    // Loading state
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: data?.total_count || 0,
      filteredItems: data?.filtered_count || 0,
      setPage,
      setPageSize: handlePageSizeChange
    },

    // Filters
    filters: {
      year: yearFilter,
      search: searchTerm,
      setYear: handleYearChange,
      setSearch: setSearchTerm
    },

    // Helpers
    getTotalDays,

    // Actions
    refetch: loadData,

    // Static data (module-level constants)
    years: YEARS
  };
}

/**
 * Hook for entitlement actions (create, update, delete)
 * Separated to keep concerns clean
 *
 * @param {Function} onSuccess - Callback after successful action
 * @returns {Object} - { createEntitlement, updateEntitlement, deleteEntitlement, checkExists, processing }
 */
export function useEntitlementActions(onSuccess) {
  const [processing, setProcessing] = useState(false);

  /**
   * Check if entitlement already exists for user/year
   */
  const checkExists = useCallback(async (userId, year) => {
    if (!userId || !year) return false;

    try {
      const { data: existing } = await supabase
        .from('leave_entitlements')
        .select('id')
        .eq('user_id', userId)
        .eq('year', year)
        .maybeSingle();

      return !!existing;
    } catch (err) {
      console.error('[useEntitlementActions] Check exists error:', err);
      return false;
    }
  }, []);

  /**
   * Create a new entitlement (single)
   */
  const createEntitlement = useCallback(async (entitlementData) => {
    if (!entitlementData?.user_id) return { success: false, error: 'Missing user_id' };

    setProcessing(true);

    try {
      // Check if already exists
      const exists = await checkExists(entitlementData.user_id, entitlementData.year);
      if (exists) {
        return { success: false, error: 'Entitlement already exists for this user and year' };
      }

      const { error } = await supabase
        .from('leave_entitlements')
        .insert({
          user_id: entitlementData.user_id,
          year: entitlementData.year,
          annual_leave_days: entitlementData.annual_leave_days,
          carry_over_days: entitlementData.carry_over_days || 0,
          entitled_to_bank_holidays: entitlementData.entitled_to_bank_holidays ?? true,
          custom_entitlements: entitlementData.custom_entitlements || {},
          status: GENERAL_STATUS.ACTIVE,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        });

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useEntitlementActions] Create error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [checkExists, onSuccess]);

  /**
   * Create multiple entitlements in bulk (single database call)
   * @param {Array} userIds - Array of user IDs
   * @param {Object} entitlementData - Common entitlement data (year, days, etc.)
   * @returns {Object} - { success, created, skipped, errors }
   */
  const createEntitlementsBulk = useCallback(async (userIds, entitlementData) => {
    if (!userIds?.length) return { success: false, created: 0, skipped: 0, errors: ['No users selected'] };

    setProcessing(true);

    try {
      const year = entitlementData.year;

      // Step 1: Check which user/year combinations already exist (single query)
      const { data: existingEntitlements, error: checkError } = await supabase
        .from('leave_entitlements')
        .select('user_id')
        .in('user_id', userIds)
        .eq('year', year);

      if (checkError) throw checkError;

      // Get set of user IDs that already have entitlements
      const existingUserIds = new Set((existingEntitlements || []).map(e => e.user_id));

      // Filter to only users who don't have entitlements yet
      const newUserIds = userIds.filter(id => !existingUserIds.has(id));
      const skippedCount = userIds.length - newUserIds.length;

      if (newUserIds.length === 0) {
        if (onSuccess) onSuccess();
        return {
          success: true,
          created: 0,
          skipped: skippedCount,
          errors: skippedCount > 0 ? ['All selected users already have entitlements for this year'] : []
        };
      }

      // Step 2: Build bulk insert data
      const now = new Date().toISOString();
      const insertData = newUserIds.map(userId => ({
        user_id: userId,
        year: entitlementData.year,
        annual_leave_days: entitlementData.annual_leave_days,
        carry_over_days: entitlementData.carry_over_days || 0,
        entitled_to_bank_holidays: entitlementData.entitled_to_bank_holidays ?? true,
        custom_entitlements: entitlementData.custom_entitlements || {},
        status: GENERAL_STATUS.ACTIVE,
        created_date: now,
        updated_date: now
      }));

      // Step 3: Single bulk insert
      const { error: insertError } = await supabase
        .from('leave_entitlements')
        .insert(insertData);

      if (insertError) throw insertError;

      if (onSuccess) onSuccess();
      return {
        success: true,
        created: newUserIds.length,
        skipped: skippedCount,
        errors: []
      };
    } catch (err) {
      console.error('[useEntitlementActions] Bulk create error:', err);
      return { success: false, created: 0, skipped: 0, errors: [err.message] };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Update an existing entitlement
   */
  const updateEntitlement = useCallback(async (entitlementId, updates) => {
    if (!entitlementId) return { success: false, error: 'Missing entitlement ID' };

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('leave_entitlements')
        .update({
          annual_leave_days: updates.annual_leave_days,
          carry_over_days: updates.carry_over_days || 0,
          entitled_to_bank_holidays: updates.entitled_to_bank_holidays ?? true,
          custom_entitlements: updates.custom_entitlements || {},
          updated_date: new Date().toISOString()
        })
        .eq('id', entitlementId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useEntitlementActions] Update error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Delete an entitlement
   */
  const deleteEntitlement = useCallback(async (entitlementId) => {
    if (!entitlementId) return { success: false, error: 'Missing entitlement ID' };

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('leave_entitlements')
        .delete()
        .eq('id', entitlementId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useEntitlementActions] Delete error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  return {
    createEntitlement,
    createEntitlementsBulk,
    updateEntitlement,
    deleteEntitlement,
    checkExists,
    processing
  };
}
